import Footer from '@/components/Footer';
import Header from '@/components/Header';
import SystemAgreementDisplay from '@/components/SystemAgreementDisplay';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { systemAgreement } from '@/data/systemAgreement';
import { fadeIn, slideUp, staggerChildren } from '@/utils/animations';
import { motion } from 'framer-motion';
import { Eye, FileText, Leaf, Recycle, Shield, Target, TreePine, Users } from "lucide-react";
import { useState } from 'react';

// Temporary function to show system creator - To be removed when system is paid
const getSystemCreatorInfo = () => {
	return {
		show: true, // Set to false to hide the creator info
		text: "System developed by Mark Gilbert Reginio. ",
		technicalInfo: "This system consists of 137,403 lines of code(93,724 lines after optimization) across 100+ files, developed within a span of 7 months. Originally developed for DENR-PENRO Marinduque, the system's architecture is designed to be potentially be implemented by other DENR offices in the future.",
		agreement: systemAgreement
	};
};

const AboutPage = () => {
	const creatorInfo = getSystemCreatorInfo();
	const [showAgreement, setShowAgreement] = useState(false);
	const [showTechnicalInfo, setShowTechnicalInfo] = useState(false);

	return (
		<div className="flex flex-col min-h-screen bg-green-50">
			<Header />
			{/* <DENRHeader /> */}
			<motion.main
				className="flex-1 mb-14"
				initial="hidden"
				animate="visible"
				variants={fadeIn}
			>
				<div className="container mx-auto px-4 py-12">
					<motion.h1
						className="text-4xl font-bold text-green-800 mb-14 text-center"
						variants={slideUp}
					>
						About Us
					</motion.h1>

					{/* Temporary Creator Info - Will be removed when system is paid */}
					{/* {creatorInfo.show && (
						<motion.div
							className="mb-8 text-center text-gray-600"
							variants={fadeIn}
						>
							<span>{creatorInfo.text}</span>
							<button
								onClick={() => setShowTechnicalInfo(true)}
								className="text-green-600 hover:text-green-700 underline font-medium"
							>
								See technical info about the system
							</button>
							<span className="mx-2">|</span>
							<button
								onClick={() => setShowAgreement(true)}
								className="text-green-600 hover:text-green-700 underline font-medium"
							>
								View System Preliminary Agreement
							</button>
						</motion.div>
					)} */}

					{/* Technical Info Dialog */}
					<Dialog open={showTechnicalInfo} onOpenChange={setShowTechnicalInfo}>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>Technical Information</DialogTitle>
								<DialogDescription>
									<p className="mt-4 text-gray-700 leading-relaxed">
										{creatorInfo.technicalInfo}
									</p>
								</DialogDescription>
							</DialogHeader>
						</DialogContent>
					</Dialog>

					{/* Agreement Dialog */}
					<Dialog open={showAgreement} onOpenChange={setShowAgreement}>
						<DialogContent className="max-w-5xl max-h-[80vh]">
							<DialogHeader>
								<DialogTitle>System Development and Transfer Agreement</DialogTitle>
							</DialogHeader>
							<div className="mt-4 overflow-y-auto pr-4 max-h-[70vh] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
								<SystemAgreementDisplay />
							</div>
						</DialogContent>
					</Dialog>

					{/* About DENR PENRO Marinduque */}
					<motion.div
						className="mb-20 bg-white rounded-lg shadow-lg p-8"
						variants={fadeIn}
					>
						<h2 className="text-3xl font-bold text-green-800 mb-8 text-center">About DENR PENRO Marinduque</h2>
						<motion.p className="text-lg mb-6" variants={slideUp}>
							The Department of Environment and Natural Resources - Provincial Environment and Natural Resources Office (DENR-PENRO) Marinduque is a government agency dedicated to environmental protection and natural resource management in the province of Marinduque, Philippines.
						</motion.p>
						<motion.div className="grid md:grid-cols-3 gap-8" variants={staggerChildren}>
							<div className="flex flex-col items-center text-center">
								<Users className="h-12 w-12 text-green-600 mb-4" />
								<h3 className="text-xl font-semibold mb-2">Leadership</h3>
								<p>Currently led by OIC For. Imelda M. Diaz</p>
							</div>
							<div className="flex flex-col items-center text-center">
								<Recycle className="h-12 w-12 text-green-600 mb-4" />
								<h3 className="text-xl font-semibold mb-2">Environmental Initiatives</h3>
								<p>Active in coastal clean-ups and mangrove planting</p>
							</div>
							<div className="flex flex-col items-center text-center">
								<FileText className="h-12 w-12 text-green-600 mb-4" />
								<h3 className="text-xl font-semibold mb-2">Services</h3>
								<p>Offers various frontline services in environmental management</p>
							</div>
						</motion.div>
						<motion.p className="text-lg mt-6" variants={slideUp}>
							DENR PENRO Marinduque is committed to implementing environmental policies, managing natural resources, and promoting sustainable development in the province. Recent collaborations with academic institutions and ongoing environmental initiatives showcase their efforts to adapt to modern challenges and engage the community in environmental conservation.
						</motion.p>
					</motion.div>

					{/* DENR Mission and Vision */}
					<motion.div
						className="mb-20 bg-white rounded-lg shadow-lg p-8"
						variants={fadeIn}
					>
						<h2 className="text-3xl font-bold text-green-800 mb-8 text-center">DENR Mission and Vision</h2>
						<div className="grid md:grid-cols-2 gap-12">
							<motion.div
								className="flex flex-col items-center text-center"
								variants={slideUp}
							>
								<Eye className="h-16 w-16 text-green-600 mb-4" />
								<h3 className="text-2xl font-semibold mb-4">Vision</h3>
								<p className="text-lg">
									A nation enjoying and sustaining its natural resources and a clean and healthy environment.
								</p>
							</motion.div>
							<motion.div
								className="flex flex-col items-center text-center"
								variants={slideUp}
							>
								<Target className="h-16 w-16 text-green-600 mb-4" />
								<h3 className="text-2xl font-semibold mb-4">Mission</h3>
								<p className="text-lg">
									To mobilize our citizenry in protecting, conserving, and managing the environment and natural resources for the present and future generations.
								</p>
							</motion.div>
						</div>
					</motion.div>

					{/* About PermitTree */}
					<motion.div
						className="mb-20 bg-white rounded-lg shadow-lg p-8"
						variants={fadeIn}
					>
						<h2 className="text-3xl font-bold text-green-800 mb-8 text-center">About PermitTree</h2>
						<motion.div
							className="grid md:grid-cols-2 gap-12 items-center"
							variants={staggerChildren}
						>
							<motion.div variants={slideUp}>
								<p className="text-lg mb-6">
									<span className="font-bold">PermitTree</span> is the official online permit issuance and management system of the Department of Environment and Natural Resources - Provincial Environment and Natural Resources Office (DENR-PENRO). Our mission is to streamline the process of obtaining permits for tree-related activities while ensuring the conservation and proper management of our country's natural resources.
								</p>
								<p className="text-lg mb-6">
									We understand the importance of balancing development with environmental protection. That's why we've created a user-friendly platform that makes it easier for individuals, businesses, and government agencies to apply for and obtain the necessary permits for their projects, all while adhering to environmental regulations.
								</p>
								<p className="text-lg">
									Our team is committed to providing efficient, transparent, and environmentally responsible services to all our users. With PermitTree, you can be confident that your tree-related activities are in compliance with local regulations and contribute to sustainable forest management practices.
								</p>
							</motion.div>
							<motion.div
								className="grid grid-cols-1 sm:grid-cols-2 gap-6 justify-items-center"
								variants={staggerChildren}
							>
								<div className="bg-green-100 p-6 rounded-lg shadow-md flex flex-col items-center text-center">
									<Leaf className="h-16 w-16 text-green-600 mb-4" />
									<h3 className="text-xl font-semibold mb-2">Eco-Friendly</h3>
									<p>Promoting sustainable practices in all our operations</p>
								</div>
								<div className="bg-green-100 p-6 rounded-lg shadow-md flex flex-col items-center text-center">
									<Shield className="h-16 w-16 text-green-600 mb-4" />
									<h3 className="text-xl font-semibold mb-2">Secure</h3>
									<p>Ensuring the safety and privacy of your data</p>
								</div>
								<div className="bg-green-100 p-6 rounded-lg shadow-md flex flex-col items-center text-center">
									<TreePine className="h-16 w-16 text-green-600 mb-4" />
									<h3 className="text-xl font-semibold mb-2">Comprehensive</h3>
									<p>Offering a wide range of tree-related permits and services</p>
								</div>
								<div className="bg-green-100 p-6 rounded-lg shadow-md flex flex-col items-center text-center">
									<svg className="h-16 w-16 text-green-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
									</svg>
									<h3 className="text-xl font-semibold mb-2">Efficient</h3>
									<p>Streamlining processes for faster permit issuance</p>
								</div>
							</motion.div>
						</motion.div>
					</motion.div>
				</div>
			</motion.main>
			<Footer />
		</div>
	);
};

export default AboutPage;
